import { Controller } from "@hotwired/stimulus";

const HOVER_DELAY_MS = 500;
const REVEAL_DELAY_MS = 80;
const READING_SPEED_PX_PER_SECOND = 57;
const MIN_DURATION_MS = 1_850;
const MAX_DURATION_MS = 11_500;
const RAMP_DURATION_MS = 420;

type MarqueePhase = "idle" | "revealed" | "scrolling" | "complete";

export default class extends Controller<HTMLElement> {
    static targets = ["track"];

    declare readonly trackTarget: HTMLElement;
    declare readonly hasTrackTarget: boolean;

    private hoverTimeoutId: number | null = null;
    private revealTimeoutId: number | null = null;
    private animation: Animation | null = null;
    private resizeObserver: ResizeObserver | null = null;
    private reducedMotionQuery: MediaQueryList | null = null;
    private fullTitle = "";

    connect(): void {
        this.fullTitle = this.element.getAttribute("title") || "";
        this.reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        this.resizeObserver = new ResizeObserver(() => this.refreshOverflowState());
        this.resizeObserver.observe(this.element);
    }

    disconnect(): void {
        this.stop();
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
    }

    start(event: PointerEvent): void {
        if (event.pointerType === "touch" || !this.hasTrackTarget) {
            return;
        }

        this.refreshOverflowState();
        if (!this.hasOverflow() || this.reducedMotionQuery?.matches) {
            return;
        }

        // The animated title replaces the native tooltip while hovering. The
        // title is restored on exit and remains available with reduced motion.
        this.element.removeAttribute("title");
        this.clearTimers();
        this.hoverTimeoutId = window.setTimeout(() => this.reveal(), HOVER_DELAY_MS);
    }

    stop(): void {
        this.clearTimers();
        this.animation?.cancel();
        this.animation = null;
        if (this.hasTrackTarget) {
            this.trackTarget.style.removeProperty("transform");
        }
        this.setPhase("idle");
        this.setEdgeState(false, false);

        if (this.fullTitle) {
            this.element.setAttribute("title", this.fullTitle);
        }
    }

    private reveal(): void {
        this.hoverTimeoutId = null;
        const distance = this.overflowDistance();
        if (distance <= 0) {
            this.refreshOverflowState();
            return;
        }

        this.setPhase("revealed");
        this.setEdgeState(false, true);
        this.revealTimeoutId = window.setTimeout(() => this.animate(distance), REVEAL_DELAY_MS);
    }

    private animate(distance: number): void {
        this.revealTimeoutId = null;
        const duration = Math.min(
            MAX_DURATION_MS,
            Math.max(MIN_DURATION_MS, (distance / READING_SPEED_PX_PER_SECOND) * 1_000),
        );
        const rampFraction = Math.min(0.16, Math.max(0.06, RAMP_DURATION_MS / duration));
        const rampDistance = Math.min(18, distance * 0.12);

        this.setPhase("scrolling");
        this.animation = this.trackTarget.animate(
            [
                {
                    transform: "translate3d(0, 0, 0)",
                    offset: 0,
                    easing: "cubic-bezier(0.42, 0, 1, 1)",
                },
                {
                    transform: `translate3d(-${rampDistance}px, 0, 0)`,
                    offset: rampFraction,
                    easing: "linear",
                },
                {
                    transform: `translate3d(-${distance - rampDistance}px, 0, 0)`,
                    offset: 1 - rampFraction,
                    easing: "cubic-bezier(0, 0, 0.58, 1)",
                },
                {
                    transform: `translate3d(-${distance}px, 0, 0)`,
                    offset: 1,
                },
            ],
            {
                duration,
                fill: "forwards",
            },
        );

        this.animation.onfinish = () => {
            this.setPhase("complete");
            this.setEdgeState(true, false);
        };

        // The left fade starts only after the track has physically moved.
        window.requestAnimationFrame(() => {
            if (this.animation?.playState === "running") {
                this.setEdgeState(true, true);
            }
        });
    }

    private refreshOverflowState(): void {
        const hasOverflow = this.overflowDistance() > 1;
        this.element.dataset.ticketOverflowMarqueeOverflow = hasOverflow ? "true" : "false";
        if (!hasOverflow && this.element.dataset.ticketOverflowMarqueePhase !== "idle") {
            this.stop();
        }
    }

    private overflowDistance(): number {
        if (!this.hasTrackTarget) {
            return 0;
        }

        return Math.max(0, this.trackTarget.scrollWidth - this.element.clientWidth);
    }

    private hasOverflow(): boolean {
        return this.element.dataset.ticketOverflowMarqueeOverflow === "true";
    }

    private setPhase(phase: MarqueePhase): void {
        this.element.dataset.ticketOverflowMarqueePhase = phase;
    }

    private setEdgeState(hasLeftOverflow: boolean, hasRightOverflow: boolean): void {
        this.element.dataset.ticketOverflowMarqueeLeft = hasLeftOverflow ? "true" : "false";
        this.element.dataset.ticketOverflowMarqueeRight = hasRightOverflow ? "true" : "false";
    }

    private clearTimers(): void {
        if (this.hoverTimeoutId !== null) {
            window.clearTimeout(this.hoverTimeoutId);
            this.hoverTimeoutId = null;
        }
        if (this.revealTimeoutId !== null) {
            window.clearTimeout(this.revealTimeoutId);
            this.revealTimeoutId = null;
        }
    }
}
